import { ActionTile } from '@dreamstack/feature-components/src/ActionTile'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { DocumentDownload } from '@dreamstack/icons/src/icon-components/DocumentDownload'
import type { Media } from '@dreamstack/payload/src/payload-types'
import { trpc, type RouterOutputs } from '@dreamstack/trpc/client'
import type { FunctionComponent } from 'react'
import { useFormattedFile } from '../hooks/useFormattedFile'

type LatestDownloadBlock = { type?: string | null }
type DownloadableFile = RouterOutputs['cms']['getLatestDownload']

export const LatestDownload: FunctionComponent<
  React.PropsWithChildren<{
    block: LatestDownloadBlock
  }>
> = ({ block }) => {
  const locale = useLocale()
  const type = block?.type

  const { data: download } = trpc.customBlockStuff.getLatestDownload.useQuery(
    { locale, type: type! },
    {
      trpc: { ssr: true },
      staleTime: Infinity,
      enabled: !!type,
    }
  )

  if (!download) return null
  return <LatestDownloadDisplay download={download} />
}

export const LatestDownloadDisplay: FunctionComponent<
  React.PropsWithChildren<{
    backgroundImageUrl?: string
    download: DownloadableFile
  }>
> = ({ download, backgroundImageUrl }) => {
  const t = useTranslation()
  const file =
    typeof download?.file === 'object' ? (download.file as Media) : undefined
  const formattedFile = useFormattedFile(file)
  if (!formattedFile) return null
  const { formattedFileSize, formattedFileType } = formattedFile
  return (
    <a href={file?.url ?? '#'} target="_blank" rel="noreferrer">
      <ActionTile
        backgroundImgUrl={backgroundImageUrl}
        heading={
          download?.title ??
          t(`accentro:downloadableFileTypes:${download?.type}`)
        }
        caption={formattedFileType ?? ''}
        fileSize={formattedFileSize}
        icon={<DocumentDownload className="h-px-24 w-px-24" />}
      />
    </a>
  )
}
